import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useApi, errMsg } from "@/lib/useApi";
import { Bookmark, ChevronLeft, ChevronRight, CloudCheck, Loader2, CloudOff, Timer } from "lucide-react";

const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

export default function ExamPlayer() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [answers, setAnswers] = useState({});
  const [visited, setVisited] = useState(new Set());
  const [idx, setIdx] = useState(0);
  const [offset, setOffset] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [saving, setSaving] = useState("saved");
  const [confirm, setConfirm] = useState(false);
  const done = useRef(false);
  const loaded = useRef(false);
  const answersRef = useRef({});
  const seq = useRef({});
  const queue = useRef(Promise.resolve());
  useEffect(() => { answersRef.current = answers; }, [answers]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    api.get(`/attempts/${id}`).then((r) => {
      if (r.data.status !== "in_progress") return nav(`/app/results/${id}`, { replace: true });
      setA(r.data);
      setAnswers(r.data.answers || {});
      setVisited(new Set(Object.keys(r.data.answers || {})));
      setOffset(Date.parse(r.data.server_now) - Date.now());
    }).catch((e) => { toast.error(errMsg(e)); nav("/app/my-exams"); });
  }, [id, api, nav]);

  const submit = useCallback(async (auto = false) => {
    if (done.current) return;
    done.current = true;
    try {
      await api.post(`/attempts/${id}/submit`);
      toast.success(auto ? "Time is up — your attempt was submitted." : "Submitted.");
    } catch (e) { toast.error(errMsg(e)); }
    nav(`/app/results/${id}`, { replace: true });
  }, [api, id, nav]);

  useEffect(() => {
    if (!a) return;
    const tick = () => {
      const rem = Date.parse(a.deadline_at) - (Date.now() + offset);
      setRemaining(rem);
      if (rem <= 0) submit(true);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [a, offset, submit]);

  const qs = a?.questions || [];
  const q = qs[idx];
  useEffect(() => { if (q) setVisited((v) => new Set(v).add(q.qid)); }, [q]);

  const save = (qid, patch) => {
    const n = (seq.current[qid] || 0) + 1;
    seq.current[qid] = n;
    const prev = answersRef.current[qid] || { selected: [], marked: false };
    const next = { ...prev, ...patch };
    answersRef.current = { ...answersRef.current, [qid]: next };
    setAnswers((s) => ({ ...s, [qid]: next }));
    setSaving("saving");
    queue.current = queue.current.then(async () => {
      try {
        await api.put(`/attempts/${id}/answer`, { question_id: qid, ...patch });
        if (seq.current[qid] === n) setSaving("saved");
      } catch (e) {
        const limit = e?.response?.status === 409 && /allows only/i.test(errMsg(e));
        if (seq.current[qid] === n) {
          answersRef.current = { ...answersRef.current, [qid]: prev };
          setAnswers((s) => ({ ...s, [qid]: prev }));
        }
        setSaving(limit ? "saved" : "error");
        toast.error(errMsg(e));
        if (e?.response?.status === 409 && /submitted|no longer active|Time is over/i.test(errMsg(e))) nav(`/app/results/${id}`, { replace: true });
      }
    });
  };

  const choose = (key) => {
    const sel = answersRef.current[q.qid]?.selected || [];
    const next = q.format === "multiple" ? (sel.includes(key) ? sel.filter((k) => k !== key) : [...sel, key].sort()) : sel[0] === key ? [] : [key];
    save(q.qid, { selected: next });
  };

  const bySection = useMemo(() => {
    const m = {};
    qs.forEach((x, i) => (m[x.section] = m[x.section] || []).push(i));
    return m;
  }, [qs]);

  if (!a || !q) return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="exam-loading">Loading exam…</div>;
  const sec = a.sections.find((s) => s.name === q.section);
  const answeredIn = (name) => (bySection[name] || []).filter((i) => answers[qs[i].qid]?.selected?.length).length;
  const sel = answers[q.qid]?.selected || [];
  const marked = !!answers[q.qid]?.marked;
  const totalAnswered = qs.filter((x) => answers[x.qid]?.selected?.length).length;
  const low = remaining !== null && remaining < 5 * 60 * 1000;

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex flex-col" data-testid="exam-player">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-display font-semibold text-sm text-slate-900 truncate">{a.family.replace("_", " ")} · {a.subjects.join(", ")}</p>
            <p className="text-xs text-slate-500 truncate" data-testid="exam-player-label">{a.label}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="autosave-indicator">
              {saving === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saving === "error" ? <CloudOff className="h-3.5 w-3.5 text-red-500" /> : <CloudCheck className="h-3.5 w-3.5 text-emerald-600" />}
              <span className="hidden sm:inline">{saving === "saving" ? "Saving…" : saving === "error" ? "Not saved" : "Saved"}</span>
            </span>
            <span className={`flex items-center gap-1.5 font-mono text-lg px-3 py-1 rounded-lg border ${low ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 text-slate-900"}`} data-testid="exam-timer-display">
              <Timer className="h-4 w-4" />{fmt(remaining ?? 0)}
            </span>
            <Button onClick={() => setConfirm(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="exam-submit-btn">Submit</Button>
          </div>
        </div>
        <div className="px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {a.sections.map((s) => (
            <button key={s.name} onClick={() => setIdx(bySection[s.name][0])} data-testid={`section-tab-${s.name.replace(/\s+/g, "-")}`}
              className={`px-4 py-2 text-sm border-b-2 whitespace-nowrap ${q.section === s.name ? "border-blue-600 text-blue-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {s.name} <span className="text-xs text-slate-400">({answeredIn(s.name)}/{s.attempts_allowed})</span>
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 grid lg:grid-cols-[1fr_320px] gap-6 p-4 sm:p-6 max-w-7xl w-full mx-auto">
        <main className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <Badge variant="outline" className="border-slate-200" data-testid="question-counter">Question {idx + 1} of {qs.length}</Badge>
            <Badge variant="outline" className="border-slate-200">Section {q.section} · +{sec.marks_correct} / {sec.marks_incorrect}</Badge>
            {q.format === "multiple" && <Badge className="bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-50">Select all correct options</Badge>}
          </div>
          <p className="text-lg leading-relaxed text-slate-900 whitespace-pre-wrap" data-testid="question-stem">{q.stem}</p>
          <div className="mt-6 space-y-3">
            {q.options.map((o) => {
              const on = sel.includes(o.key);
              return (
                <button key={o.key} onClick={() => choose(o.key)} data-testid={`question-option-${o.key}`}
                  className={`w-full text-left flex items-start gap-3 p-4 rounded-lg border transition-colors ${on ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <span className={`h-6 w-6 shrink-0 grid place-items-center text-xs font-semibold border ${q.format === "multiple" ? "rounded-md" : "rounded-full"} ${on ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300 text-slate-600"}`}>{o.key}</span>
                  <span className="text-slate-800">{o.text}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
              <Button variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="prev-question-btn"><ChevronLeft className="h-4 w-4 mr-1" />Previous</Button>
              <Button variant="ghost" disabled={!sel.length} onClick={() => save(q.qid, { selected: [] })} data-testid="clear-answer-btn">Clear</Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => save(q.qid, { marked: !marked })} data-testid="mark-for-review-btn"
                className={marked ? "border-violet-300 bg-violet-50 text-violet-700" : ""}><Bookmark className="h-4 w-4 mr-1" />{marked ? "Marked" : "Mark for review"}</Button>
              <Button disabled={idx === qs.length - 1} onClick={() => setIdx(idx + 1)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-next-btn">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
            </div>
          </div>
        </main>

        <aside className="bg-white border border-slate-200 rounded-xl p-5 h-fit lg:sticky lg:top-32" data-testid="question-palette">
          <p className="text-sm font-medium text-slate-900">{totalAnswered} of {qs.length} answered</p>
          {a.sections.map((s) => (
            <div key={s.name} className="mt-4">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Section {s.name}</p>
              <div className="grid grid-cols-6 gap-1.5">
                {(bySection[s.name] || []).map((i) => {
                  const st = answers[qs[i].qid] || {};
                  const cls = st.marked ? "bg-violet-100 border-violet-300 text-violet-800" : st.selected?.length ? "bg-emerald-100 border-emerald-300 text-emerald-800" : visited.has(qs[i].qid) ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-white border-slate-200 text-slate-600";
                  return (
                    <button key={i} onClick={() => setIdx(i)} data-testid={`palette-q-${i + 1}`}
                      className={`h-8 text-xs rounded-md border ${cls} ${i === idx ? "ring-2 ring-blue-500" : ""} relative`}>
                      {i + 1}{st.marked && st.selected?.length ? <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-emerald-500" /> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="mt-5 grid grid-cols-2 gap-2 text-xs text-slate-600">
            <Legend c="bg-emerald-100 border-emerald-300" t="Answered" /><Legend c="bg-violet-100 border-violet-300" t="Marked" />
            <Legend c="bg-amber-50 border-amber-300" t="Visited, unanswered" /><Legend c="bg-white border-slate-200" t="Not visited" />
          </div>
        </aside>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit this attempt?</AlertDialogTitle>
            <AlertDialogDescription>You answered {totalAnswered} of {qs.length}. You cannot change answers after submission.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="submit-cancel-btn">Keep working</AlertDialogCancel>
            <AlertDialogAction onClick={() => submit(false)} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-confirm-btn">Submit test</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Legend = ({ c, t }) => <span className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded border ${c}`} />{t}</span>;
